import { createAsyncThunk } from "@reduxjs/toolkit";

import * as basketAPI from "../../../api/basket";
import { IBasket } from "../../../models/catalogue";
import { IProductAPIURL } from "../../../models/nominals";
import { loadBasket } from "./loadBasket";

/**
 * Arguments for addBasketLines thunk.
 */
interface AddBasketLinesArgs {
    lines: Array<{ productURL: IProductAPIURL; qty?: number }>;
}

/**
 * Add multiple products to the basket.
 *
 * This thunk adds multiple products to the user's basket sequentially,
 * then reloads the basket once to reflect all changes. This is useful
 * for bulk operations like adding gift bundles or promotional sets.
 *
 * @param {Array<{productURL: IProductAPIURL; qty?: number}>} args.lines - Array of products to add with optional quantities
 * @returns {Promise<IBasket>} Promise resolving to the reloaded basket
 *
 * @example
 * // Add multiple products at once
 * dispatch(addBasketLines({
 *   lines: [
 *     { productURL: product1.url, qty: 2 },
 *     { productURL: product2.url },
 *   ]
 * }));
 *
 * @sideEffects
 * - Makes sequential API calls to add each product via basketAPI.addProduct
 * - Dispatches loadBasket thunk to reload basket data after all adds
 * - Updates `data.basket` with reloaded basket via extraReducers
 */
export const addBasketLines = createAsyncThunk(
    "checkout/addBasketLines",
    async (args: AddBasketLinesArgs, { dispatch }): Promise<IBasket> => {
        for (const { productURL, qty } of args.lines) {
            await basketAPI.addProduct(productURL, qty);
        }
        const result = await dispatch(loadBasket()).unwrap();
        return result.basket;
    },
);
